import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import { ToastContainer, toast } from "react-toastify";
import { BsFillSuitHeartFill } from "react-icons/bs";

// Components
import Shearchbar from "./Shearchbar";
import Navbar from "./Navbar";
import Footer from "./Footer";
import Manubar from "./Manubar";
import { useCart } from "./CartContext";

// Styles
import "./loader.css";

// Constants
const ITEMS_PER_PAGE = 8;
const API_BASE_URL = `${process.env.REACT_APP_API}website`;
const UPLOADS_BASE_URL = `${process.env.REACT_APP_API}uploads`;

const Shops_category = () => {
  const navigate = useNavigate();
  const { triggerCartRefresh } = useCart();

  // State management
  const [slideIndex, setSlideIndex] = useState(0);
  const [images, setImages] = useState([null]);
  const [subcategoryList, setSubcategoryList] = useState([]);
  const [modelProductDetails, setModelProductDetails] = useState([]);
  const [counter, setCounter] = useState(1);
  const [showProductsList, setShowProductsList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentProductId, setCurrentProductId] = useState(null);
  const [indexValue, setIndexValue] = useState(0);
  const [loading, setLoading] = useState(false);

  // User data from storage
  const token = secureLocalStorage.getItem("n_token");
  const cartListId = secureLocalStorage.getItem("n_cartlist_id");
  const userId = secureLocalStorage.getItem("n_id");
  const subcategoryListId = secureLocalStorage.getItem(
    "n_sub_category_list_id"
  );
  const subcategoryProductsList = secureLocalStorage.getItem(
    "sub_category_products_id"
  );

  // Pagination calculations
  const totalPages = Math.ceil(showProductsList.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentData = showProductsList.slice(startIndex, endIndex);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  // Image slider functions
  const plusSlides = (n) => {
    setSlideIndex(
      (prevIndex) => (prevIndex + n + images.length) % images.length
    );
  };

  const currentSlide = (n) => {
    setSlideIndex(n);
  };

  // API call wrapper with error handling
  const apiCall = async (endpoint, data = null, method = "post") => {
    try {
      const config = {
        method,
        url: `${API_BASE_URL}/${endpoint}`,
        ...(data && { data }),
        ...(token && { headers: { token } }),
      };

      const response = await axios(config);
      return response.data;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  };

  // Subcategory selection handler
  const handleSubcategorySelect = (subcategoryId) => {
    secureLocalStorage.setItem("sub_category_products_id", subcategoryId);
  };

  // Single product modal handler
  const handleSingleProductModal = useCallback(
    async (productId) => {
      setCurrentProductId(productId);
      setLoading(true);

      try {
        const data = {
          product_id: productId,
          user_id: userId,
        };

        const result = await apiCall("product_details", data);

        if (result.result) {
          setImages(result.data[0]?.product_image || []);
          setModelProductDetails(result.data);
        }
      } catch (error) {
        toast.error("Failed to load product details");
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  // Single product page navigation
  const handleSingleProductPage = (productId) => {
    secureLocalStorage.setItem("n_singleproducts", productId);
    navigate("/single_products");
  };

  // Close modal handler
  const handleCloseModal = () => {
    setModelProductDetails([]);
    secureLocalStorage.setItem("n_singleproducts", null);
  };

  // Quantity handlers
  const handleQuantityIndex = (index) => {
    setIndexValue(index);
  };

  const handleIncrement = () => {
    setCounter((prev) => prev + 1);
  };

  const handleDecrement = () => {
    setCounter((prev) => (prev > 1 ? prev - 1 : 1));
  };

  // Add to cart functionality
  const handleAddToCart = async (product, isModal = false) => {
    if (!token) {
      toast.info("Please login to add items to cart");
      return;
    }

    try {
      const priceIndex = isModal ? indexValue : 0;
      const quantity = isModal ? counter : 1;
      const totalPrice = isModal
        ? product.price[priceIndex] * counter
        : product.price[0];

      const cartData = {
        cart_id: cartListId,
        user_id: userId,
        product_id: product._id || product.product_id,
        quantity: product.quantity[priceIndex],
        no_of_product: quantity,
        mrp: product.mrp,
        price: product.price[priceIndex],
        total_mrp_ammount: product.mrp,
        total_price_ammount: totalPrice,
        product_name: product.product_name,
        product_image: product.product_image[0],
      };

      const result = await apiCall("add_to_cart", cartData);

      if (result.result) {
        secureLocalStorage.setItem("n_cartlist_id", result.data._id);
        triggerCartRefresh();

        if (isModal) {
          handleSingleProductModal(currentProductId);
        } else {
          // Refresh product lists
          fetchProductsBySubcategory();
        }

        toast.success("Product added to cart successfully ✅");
      } else {
        toast.error(result.msg || "Failed to add item to cart");
        
      }
    } catch (error) {
      toast.error("Something went wrong while adding to cart");
      
    }
  };

  // Favorite product handler
  const handleAddFavorite = async (productId, favStatus) => {
    if (!token) {
      toast.info("Please login to manage favorites");
      return;
    }

    try {
      const data = {
        user_id: userId,
        product_id: currentProductId || productId,
      };

      const result = await apiCall("add_favourite_product", data);
        toast.success(result.msg);
        triggerCartRefresh();
        fetchProductsBySubcategory();

        if (currentProductId) {
          handleSingleProductModal(currentProductId);
        }
      
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  // Data fetching functions
  const fetchSubCategories = useCallback(async () => {
    if (!subcategoryListId) return;

    try {
      const data = { category_id: subcategoryListId };
      const result = await apiCall("sub_category_list", data);

      if (result.result && result.data.length > 0) {
        const firstSubCategoryId = result.data[0].sub_category_id;
        secureLocalStorage.setItem(
          "sub_category_products_id",
          firstSubCategoryId
        );
        setSubcategoryList(result.data);
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
  }, [subcategoryListId]);

  const fetchProductsBySubcategory = useCallback(async () => {
    if (!subcategoryProductsList) return;

    try {
      const data = { sub_category_id: subcategoryProductsList, user_id: userId };
      const result = await apiCall("subcategory_product_list", data);

      if (result.result) {
        setShowProductsList(result.data);
      }
    } catch (error) {
      console.error("Error fetching products by subcategory:", error);
    }
  }, [subcategoryProductsList]);

  const fetchAllProducts = useCallback(async () => {
    try {
      const result = await apiCall("all_product_list", null, "get");
      if (result.result) {
      }
    } catch (error) {
      console.error("Error fetching all products:", error);
    }
  }, []);

  useEffect(() => {
    fetchSubCategories();
  }, [fetchSubCategories]);

  useEffect(() => {
    fetchProductsBySubcategory();
  }, [fetchProductsBySubcategory]);

  useEffect(() => {
    fetchAllProducts();
  }, [fetchAllProducts]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Render helpers
  const renderFavoriteButton = (product, isModal = false) => {
    if (!token) {
      return (
        <li className="action whish-list">
          <Link to="/login">
            <i className="lar la-heart" />
          </Link>
        </li>
      );
    }

    const isFavorite = isModal
      ? product.favourite_status === "1"
      : product.favourite_status === "1";

    return (
      <li className="action whish-list">
        <BsFillSuitHeartFill
          style={{
            color: isFavorite ? "red" : "currentColor",
            cursor: "pointer",
          }}
          onClick={() =>
            handleAddFavorite(
              product._id || product.product_id,
              product.favourite_status
            )
          }
        />
      </li>
    );
  };

  const renderAddToCartButton = (product, isModal = false) => {
    const isInCart = product.cart_status === "1" || product.cart_status === 1;

    if (isInCart) {
      return (
        <button
          onClick={() => navigate("/cardlist")}
          className={`btn btn-success ${
            isModal ? "btn-lg w-100 py-3" : "add-to-cart-btn"
          }`}
        >
          {isModal ? "Already in Cart (Go to Cart)" : "Already in Cart"}
        </button>
      );
    }

    if (!token) {
      return (
        <button
          className={`btn btn-dark ${
            isModal ? "modal-add-to-cart-btn" : "add-to-cart-btn"
          }`}
        >
          <Link to="/login" className="text-white text-decoration-none">
            Add to cart
          </Link>
        </button>
      );
    }

    return (
      <button
        className={`btn btn-dark ${
          isModal ? "modal-add-to-cart-btn" : "add-to-cart-btn"
        }`}
        onClick={() => handleAddToCart(product, isModal)}
      >
        Add to cart
      </button>
    );
  };

  const renderProductImage = (product) => (
    <img
      className="product-image p-2"
      src={`${UPLOADS_BASE_URL}/${product.product_image}`}
      width="100%"
      height="200px"
      alt={product.product_name}
      loading="lazy"
    />
  );

  const renderProductCard = (product) => (
    <div
      className="col-sm-6 col-md-4 col-lg-4 col-xl-3 mb-7"
      style={{ zIndex: "0", paddingBottom: "50px" }}
    >
      <div className="product-card" style={{ zIndex: "0" }}>
        <span className="badge bg-success product-badge mt-1 ml-1">{product.offer_percent}%</span>

        <div className="product-thumb-nail">
          {renderProductImage(product)}

          <ul className="actions">
            {renderFavoriteButton(product)}

            <li className="action quick-view">
              {token ? (
                <button data-toggle="modal" data-target="#shop_category">
                  <i
                    className="las la-eye"
                    onClick={() => handleSingleProductModal(product.product_id)}
                  />
                </button>
              ) : (
                <Link to="/login">
                  <button>
                    <i className="las la-eye" />
                  </button>
                </Link>
              )}
            </li>
          </ul>
        </div>

        <div className="product-content">
          <h4 className="product-sub-title">
            <a href="#">Fresh Vegetables</a>
          </h4>

          {token ? (
            <h3 className="product-title">
              <span onClick={() => handleSingleProductPage(product.product_id)}>
                {product.product_name}
              </span>
            </h3>
          ) : (
            <Link to="/login">
              <h3 className="product-title">
                <span>{product.product_name}</span>
              </h3>
            </Link>
          )}

          <div className="product-price-wrapp">
            <span className="product-price-on-sale pl-2">
              ₹{product.price?.[0]}
            </span>
            <span className="product-regular-price">₹{product.mrp}</span>
            
          </div>

          <div className="product-cart-btn-wrap">
            {renderAddToCartButton(product)}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPagination = () => (
    <div className="text-center p-4">
      <button
        className="pagination-button"
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
      >
        Previous
      </button>

      {pageNumbers.map((page) => (
        <button
          key={page}
          onClick={() => handlePageChange(page)}
          className={`pagination-button ${
            page === currentPage ? "pagiactive" : ""
          }`}
        >
          {page}
        </button>
      ))}

      <button
        className="pagination-button"
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
      >
        Next
      </button>
    </div>
  );

  const renderImageGallery = () => (
    <div className="gallery position-relative">
      <div className="container">
        <div className="mySlides">
          <div className="numbertext">
            {slideIndex + 1} / {images.length}
          </div>
          <img
            src={`${UPLOADS_BASE_URL}/${images[slideIndex]}`}
            style={{ width: "100%", height: "400px" }}
            alt={`Product view ${slideIndex + 1}`}
          />
        </div>

        <a
          className="prev"
          style={{ cursor: "pointer" }}
          onClick={() => plusSlides(-1)}
        >
          ❮
        </a>
        <a
          className="next"
          style={{ float: "right", cursor: "pointer" }}
          onClick={() => plusSlides(1)}
        >
          ❯
        </a>

        <div className="row justify-content-center">
          {images.map((image, index) => (
            <img
              key={index}
              className={`img-fluid shadow m-1 demo cursor${
                index === slideIndex ? " active" : ""
              }`}
              src={`${UPLOADS_BASE_URL}/${image}`}
              style={{ width: "20%", height: "100px" }}
              onClick={() => currentSlide(index)}
              alt={`Thumbnail ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <ToastContainer position="top-center" />

      {/* Modals */}
      <div className="modal fade offcanvas-modal" id="exampleModal">
        <div className="modal-dialog offcanvas-dialog">
          <div className="modal-content">
            <Manubar />
          </div>
        </div>
      </div>

      <header>
        <Shearchbar />
        <Navbar />
      </header>

      {/* Breadcrumb */}
      <nav className="breadcrumb-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <ol className="breadcrumb bg-transparent m-0 p-0 align-items-center">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Fresh Food
                </li>
              </ol>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="shop-product-tab section-mb">
        <div className="container">
          <div className="row">
            {/* Sidebar */}
            <div className="col-lg-3 order-lg-first">
              <div className="widgets">
                <div className="widget-card">
                  <h3 className="title">
                    <span>Categories</span>
                  </h3>
                  <div className="search-filter">
                    <form>
                      <div className="widget-inner">
                        {subcategoryList.length === 0 ? (
                          <p>No Subcategories Found</p>
                        ) : (
                          subcategoryList.map((item) => (
                            <div key={item.sub_category_id}>
                              <input
                                onClick={() =>
                                  handleSubcategorySelect(item.sub_category_id)
                                }
                                type="radio"
                                id={`subcat-${item.sub_category_id}`}
                                className="mr-2"
                                style={{
                                  backgroundColor: "green",
                                  cursor: "pointer",
                                }}
                                name="subcategory"
                              />
                              <label htmlFor={`subcat-${item.sub_category_id}`}>
                                {item.sub_category_name}
                              </label>
                            </div>
                          ))
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className="col-lg-9">
              <div className="tab-content" id="myTabContent">
                <div
                  className="tab-pane fade show active"
                  id="home"
                  role="tabpanel"
                >
                  <div className="row grid-view mb-n7">
                    {currentData.length > 0 ? (
                      currentData.map(renderProductCard)
                    ) : (
                      <div className="row justify-content-center pb-5">
                        <div className="loader"></div>
                      </div>
                    )}

                    {renderPagination()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <section className="service-section section-mt section-mb">
        <div className="container">
          <div className="services">
            <div className="row mb-n7">
              {[
                {
                  icon: "service1.png",
                  title: "100% SECURE PAYMENTS",
                  desc: "Moving your card details to a much more secured place",
                },
                {
                  icon: "service2.png",
                  title: "TRUSTPAY",
                  desc: "100% Payment Protection. Easy Return Policy",
                },
                {
                  icon: "service3.png",
                  title: "HELP CENTER",
                  desc: "Got a question? Look no further. Browse our FAQs or submit your query here.",
                },
                {
                  icon: "service4.png",
                  title: "Express Shipping",
                  desc: "Fast, reliable delivery from global warehouses",
                },
              ].map((service, index) => (
                <div key={index} className="col-sm-6 col-lg-3 mb-7">
                  <div className="service-item">
                    <img
                      className="service-icon"
                      src={`assets/images/icon/${service.icon}`}
                      alt={service.title}
                    />
                    <h3 className="service-title">{service.title}</h3>
                    <p>{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Product Modal */}
      <div className="modal" id="shop_category">
        <div className="modal-dialog modal-dialog-centered product-modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <button
                type="button"
                style={{ fontSize: "35px" }}
                className="close"
                data-dismiss="modal"
                onClick={handleCloseModal}
              >
                &times;
              </button>
            </div>

            {loading ? (
              <div className="row justify-content-center pb-5">
                <div className="loader"></div>
              </div>
            ) : modelProductDetails?.length > 0 ? (
              <div className="modal-body">
                <div className="row mb-n7">
                  <div className="col-md-5 mb-7">
                    <div className="modal-gallery-slider">
                      {renderImageGallery()}
                    </div>
                  </div>

                  <div className="col-md-7 mb-7">
                    <div
                      className="modal-product-des"
                      style={{ marginTop: "25px" }}
                    >
                      <h3 className="modal-product-title">
                        {modelProductDetails[0]?.product_name}
                      </h3>
                      <h3 className="modal-product-sub-title">
                        Category: {modelProductDetails[0]?.category_name}
                      </h3>

                      <div className="reviews">
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className="las la-star" />
                        ))}
                      </div>

                      <div className="pt-1">
                        <span style={{ fontSize: "20px" }}>Offer</span>
                        <span className="badge badge-lg bg-dark">
                          {modelProductDetails[0]?.offer_percent}%
                        </span>
                      </div>

                      <div className="product-price-wrapp-lg">
                        <span className="product-price-on-sale-lg">
                          ₹{modelProductDetails[0]?.price[indexValue] * counter}
                        </span>
                        <span className="product-regular-price-lg pl-2">
                          ₹{modelProductDetails[0]?.mrp}
                        </span>
                      </div>

                      <div className="product-description-short pb-7 mb-7">
                        <ul>
                          <li>{modelProductDetails[0]?.description}</li>
                        </ul>

                        {modelProductDetails[0]?.quantity?.map(
                          (item, index) => (
                            <button
                              key={index}
                              className="btn btn-success m-1"
                              onClick={() => handleQuantityIndex(index)}
                            >
                              {item}
                            </button>
                          )
                        )}
                      </div>

                      <div className="product-add-to-cart">
                        <span className="control-label">Quantity</span>
                        <div className="product-count style d-flex my-7">
                          <div className="count d-flex">
                            <input
                              disabled
                              type="number"
                              min={1}
                              max={100}
                              value={counter}
                            />
                            <div className="button-group">
                              <button className="count-btn increment">
                                <span
                                  className="las la-angle-up"
                                  onClick={handleIncrement}
                                />
                              </button>
                              <button className="count-btn decrement">
                                <span
                                  className="las la-angle-down"
                                  onClick={handleDecrement}
                                />
                              </button>
                            </div>
                          </div>

                          <div>
                            {renderAddToCartButton(
                              modelProductDetails[0],
                              true
                            )}
                          </div>
                        </div>

                        <div className="product-add-to-card">
                          {renderFavoriteButton(modelProductDetails[0], true)}
                        </div>

                        <div className="product-social-sharing mt-7">
                          <span>Share</span>
                          <ul>
                            <li className="facebook">
                              <a
                                href="#"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <i className="lab la-facebook-f" />
                              </a>
                            </li>
                            <li className="twitter">
                              <a
                                href="#"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <i className="lab la-twitter" />
                              </a>
                            </li>
                            <li className="pinterest">
                              <a
                                href="#"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <i className="lab la-pinterest" />
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="row justify-content-center pb-5">
                <div className="loader"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shops_category;
